/// <amd-module name="Wiki/Follow/FollowComponent" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Button } from "VSSUI/Button";
import { Pill, PillSize } from "VSSUI/Pill";

import { IFollowsService } from "Notifications/Common/Contracts/FollowsService";
import {
    ArtifactFilter,
    ISubscriptionFilter,
    NotificationSubscription,
    SubscriptionQuery,
    SubscriptionQueryCondition,
    SubscriptionQueryFlags
} from "Notifications/Common/Contracts/Notification";
import { INotificationRestClient } from "Notifications/Common/RestClient/Notification";
import * as Resources from "Wiki/Follow/Resources";
import { getSafeId } from "VSSUI/Util";

const Follow = "Follow";
const NegativeFollow = "NegativeFollow";

const enum WikiPageFollowStatus {
    NotFollowing = 0,
    Following
}

interface IFollowButtonState {
    wikiPageFollowStatus?: WikiPageFollowStatus;
    followersCount?: number;
}

interface IFollowButtonProps {
    pageId: number;
    wikiId: string;
    projectId: string;
    /**
     * unfollowPage param is received as true when in url there is a param unfollow=true.
     * It will then first unfollow the page and then loads the status
     */
    unfollowPage: boolean;
    /**
     * if refreshFollowStatus param is true, then we will first refresh the
     * subscriptions cache in FollowService before checking the status.
     */
    refreshFollowStatus?: boolean;
    onFollowStatusRefreshed?: () => void;
    /**
     * Optional class name to add to the button element.
     */
    className?: string;
    /**
     * Optional class name to add to the button icon.
     */
    buttonIconClassName?: string;

    unsetUnfollowPageOption(): void;
}

const ArtifactType = "WikiPageId";

const enum FollowAction {
    Follow = "Follow",
    Unfollow = "Unfollow"
}

export class FollowComponent extends VssComponent<IFollowButtonProps, IFollowButtonState> {
    private readonly _followService: IFollowsService;

    constructor(props: IFollowButtonProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
        this._followService = context.pageContext.getService<IFollowsService>("IFollowsService");
    }

    public componentDidUpdate(prevProps: Readonly<IFollowButtonProps>, prevState: Readonly<IFollowButtonState>): void {
        super.componentDidUpdate(prevProps, prevState);
        if (this.props.pageId !== prevProps.pageId) {
            this._performFollowDataLoad();
        }
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._performFollowDataLoad();
    }

    public render(): JSX.Element | null {
        const { wikiPageFollowStatus, followersCount } = this.state;
        return wikiPageFollowStatus !== undefined && followersCount != undefined ? (
            <div className="flex-row flex-center flex-noshrink">
                <Button
                    className={css("page-follow-button", this.props.className)}
                    iconProps={{
                        className: this.props.buttonIconClassName,
                        iconName: wikiPageFollowStatus === WikiPageFollowStatus.Following ? "Hide2" : "View"
                    }}
                    onClick={this._onButtonClick}
                    ariaLabel={Resources.Follow}
                    ariaDescribedBy="page-follow-button-description"
                    ariaPressed={wikiPageFollowStatus === WikiPageFollowStatus.Following}
                >
                    <span className="body-m">{wikiPageFollowStatus === WikiPageFollowStatus.Following ? Resources.Unfollow : Resources.Follow}</span>
                </Button>
                <span id={getSafeId("page-follow-button-description")} aria-label={format(Resources.FollowerPillText, followersCount)}></span>
                <Pill ariaHidden={true} className="page-follower-count" size={PillSize.regular} excludeTabStop={true}>
                    {followersCount}
                </Pill>
            </div>
        ) : null;
    }

    private _performFollowDataLoad(): void {
        if (this.props.refreshFollowStatus) {
            this._followService.refresh(ArtifactType).then(() => {
                this._loadFollowData();
                if (this.props.onFollowStatusRefreshed) {
                    this.props.onFollowStatusRefreshed();
                }
            });
        } else {
            this._loadFollowData();
        }
    }

    private _loadFollowData(): void {
        const isFollowingPromise = this._followService.isFollowing(ArtifactType, this._getFollowArtifactId());
        const followersCountPromise = this._getFollowersCount();
        this.trackPromise(Promise.all([isFollowingPromise, followersCountPromise])).promise.then(([isFollowing, followersCount]) => {
            const followStatus = isFollowing ? WikiPageFollowStatus.Following : WikiPageFollowStatus.NotFollowing;
            this.setState({ wikiPageFollowStatus: followStatus, followersCount: followersCount });
            if (this.props.unfollowPage) {
                if (isFollowing) {
                    // unfollow page if user is following
                    this._unFollowPage(true);
                }
                this.props.unsetUnfollowPageOption();
            }
        });
    }

    private _onButtonClick = (): void => {
        const { wikiPageFollowStatus } = this.state;
        if (wikiPageFollowStatus === WikiPageFollowStatus.Following) {
            this._unFollowPage();
        } else {
            this._followPage();
        }
    };

    private _followPage = (): void => {
        this.setState((state, prop) => {
            const followCount = state.followersCount! + 1;
            return { followersCount: followCount, wikiPageFollowStatus: WikiPageFollowStatus.Following };
        });
        this._followService.follow(ArtifactType, this._getFollowArtifactId());
        this._publishTelemetry(Follow, { followAction: FollowAction.Follow });
    };

    private _unFollowPage = (usingUnfollowUrl?: boolean, callBack?: () => void): void => {
        this.setState((state, prop) => {
            let followCount = state.followersCount! - 1;
            // Adding sanity of unlikely corrupt count status
            if (followCount < 0) {
                followCount = 0;
                this._publishTelemetry(NegativeFollow);
            }
            return { followersCount: followCount, wikiPageFollowStatus: WikiPageFollowStatus.NotFollowing };
        });

        this._followService.unfollow(ArtifactType, this._getFollowArtifactId()).then(() => {
            if (callBack) {
                callBack();
            }
        });
        this._publishTelemetry(Follow, { followAction: FollowAction.Unfollow, unfollowUsingUrl: usingUnfollowUrl });
    };

    private _getFollowersCount(): Promise<number> {
        const wikiPageFilter = {
            artifactId: this._getFollowArtifactId(),
            artifactType: ArtifactType,
            type: "Artifact"
        } as ArtifactFilter;

        const wikiPageCondition: SubscriptionQueryCondition = {
            filter: wikiPageFilter as ISubscriptionFilter
        } as SubscriptionQueryCondition;

        const subQuery: SubscriptionQuery = {
            conditions: [wikiPageCondition],
            queryFlags: SubscriptionQueryFlags.AlwaysReturnBasicInformation
        };

        const client = this.context.pageContext.getRestClient<INotificationRestClient>("INotificationRestClient");
        return client.querySubscriptions(subQuery).then((subscriptions: NotificationSubscription[]) => {
            return subscriptions.length;
        });
    }

    private _getFollowArtifactId(): string {
        return format("{0}/{1}/{2}", this.props.projectId, this.props.wikiId, this.props.pageId.toString());
    }

    private _publishTelemetry = (feature: string, properties?: { [key: string]: any }): void => {
        const service = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        service.publishEvent("Wiki", feature, {
            pageId: this.props.pageId,
            projectId: this.props.projectId,
            wikiId: this.props.wikiId,
            ...properties
        });
    };
}

VssComponent.register("Wiki.FollowComponent", FollowComponent);
